/**
 * Mock profiles — owner profiles for every provider plus a pool of customer profiles.
 * The "dev" profiles (customer/provider/admin) match the devSession fixed UUIDs.
 */

import type { Tables } from '@/lib/db.types'
import { customerUserId, providerUserId, DEV_USER_IDS } from './ids'

type Profile = Tables<'profiles'>

const NOW = new Date('2026-05-01T08:00:00.000Z').toISOString()

interface ProfileSeed {
  full_name: string
  email: string
  phone: string | null
  avatar_url: string | null
  role: 'customer' | 'provider' | 'admin'
}

// ── Provider user profiles (1 per provider) ────────────────────────────
export const PROVIDER_USER_PROFILES: Profile[] = [
  { full_name: 'Anders Lindberg',  email: 'anders@flyttkungen.se',        phone: '+46701234561', avatar_url: null, role: 'provider' },
  { full_name: 'Maria Andersson',  email: 'maria@stadexperten.se',        phone: '+46701234562', avatar_url: null, role: 'provider' },
  { full_name: 'Erik Johansson',   email: 'erik@elek-stockholm.se',       phone: '+46701234563', avatar_url: null, role: 'provider' },
  { full_name: 'Sofia Karlsson',   email: 'sofia@vvsteamet.se',           phone: '+46701234564', avatar_url: null, role: 'provider' },
  { full_name: 'Johan Bergström',  email: 'johan@snickeriab.se',          phone: '+46701234565', avatar_url: null, role: 'provider' },
  { full_name: 'Lisa Nyström',     email: 'lisa@maleripartners.se',       phone: '+46701234566', avatar_url: null, role: 'provider' },
  { full_name: 'Mikael Holm',      email: 'mikael@gotradgard.se',         phone: '+46701234567', avatar_url: null, role: 'provider' },
  { full_name: 'Anna Sjögren',     email: 'anna@hundpensionatet.se',      phone: '+46701234568', avatar_url: null, role: 'provider' },
  { full_name: 'Daniel Pettersson',email: 'daniel@datorhjalpen.se',       phone: '+46701234569', avatar_url: null, role: 'provider' },
  { full_name: 'Emma Wallin',      email: 'emma@lararemma.se',            phone: '+46701234570', avatar_url: null, role: 'provider' },
  { full_name: 'Henrik Östberg',   email: 'henrik@bohagsflytt-malmo.se',  phone: '+46701234571', avatar_url: null, role: 'provider' },
  { full_name: 'Isabella Strand',  email: 'bella@golvhantverk.se',        phone: '+46701234572', avatar_url: null, role: 'provider' },
  { full_name: 'Olof Hedman',      email: 'olof@vp-installation.se',      phone: '+46701234573', avatar_url: null, role: 'provider' },
  { full_name: 'Petra Lundqvist',  email: 'petra@haerlandet.se',          phone: '+46701234574', avatar_url: null, role: 'provider' },
  { full_name: 'Rickard Berg',     email: 'rickard@bergmoblering.se',     phone: '+46701234575', avatar_url: null, role: 'provider' },
  { full_name: 'Klara Forsberg',   email: 'klara@trappspecialisten.se',   phone: '+46701234576', avatar_url: null, role: 'provider' },
  { full_name: 'Måns Ek',          email: 'mans@kakelsattaren.se',        phone: '+46701234577', avatar_url: null, role: 'provider' },
  { full_name: 'Nora Lindgren',    email: 'nora@spraklaget.se',           phone: '+46701234578', avatar_url: null, role: 'provider' },
  { full_name: 'Viktor Sandberg',  email: 'viktor@malmovvs.se',           phone: '+46701234579', avatar_url: null, role: 'provider' },
  { full_name: 'Linnea Holm',      email: 'linnea@piano-flytt.se',        phone: '+46701234580', avatar_url: null, role: 'provider' },
].map<Profile>((p, i) => ({
  id: providerUserId(i + 1),
  email: p.email,
  full_name: p.full_name,
  phone: p.phone,
  avatar_url: p.avatar_url,
  role: p.role as 'customer' | 'provider' | 'admin',
  bankid_verified: true,
  bankid_pnr_hash: null,
  preferred_language: 'sv',
  created_at: NOW,
  updated_at: NOW,
}))

// ── Customer profiles (besides the dev customer) ──────────────────────
const CUSTOMER_SEEDS: ProfileSeed[] = [
  { full_name: 'Karin Sundström',  email: 'karin@example.se',   phone: '+46731122001', avatar_url: null, role: 'customer' },
  { full_name: 'Magnus Holm',      email: 'magnus@example.se',  phone: '+46731122002', avatar_url: null, role: 'customer' },
  { full_name: 'Astrid Norén',     email: 'astrid@example.se',  phone: '+46731122003', avatar_url: null, role: 'customer' },
  { full_name: 'Filip Sköld',      email: 'filip@example.se',   phone: '+46731122004', avatar_url: null, role: 'customer' },
  { full_name: 'Hanna Wikström',   email: 'hanna@example.se',   phone: '+46731122005', avatar_url: null, role: 'customer' },
  { full_name: 'Oscar Lund',       email: 'oscar@example.se',   phone: '+46731122006', avatar_url: null, role: 'customer' },
  { full_name: 'Tilde Falk',       email: 'tilde@example.se',   phone: '+46731122007', avatar_url: null, role: 'customer' },
  { full_name: 'Albin Hellström',  email: 'albin@example.se',   phone: '+46731122008', avatar_url: null, role: 'customer' },
]

export const CUSTOMER_PROFILES: Profile[] = CUSTOMER_SEEDS.map((p, i) => ({
  id: customerUserId(i + 1),
  email: p.email,
  full_name: p.full_name,
  phone: p.phone,
  avatar_url: p.avatar_url,
  role: p.role as 'customer' | 'provider' | 'admin',
  bankid_verified: true,
  bankid_pnr_hash: null,
  preferred_language: 'sv',
  created_at: NOW,
  updated_at: NOW,
}))

// ── Dev session profiles (overridden by devSession metadata at runtime) ─
export const DEV_PROFILES: Record<'customer' | 'provider' | 'admin', Profile> = {
  customer: {
    id: DEV_USER_IDS.customer,
    email: 'test-kund@hjalptorget.dev',
    full_name: 'Sigge Reichard',
    phone: '+46739000001',
    avatar_url: null,
    role: 'customer',
    bankid_verified: true,
    bankid_pnr_hash: null,
    preferred_language: 'sv',
    created_at: NOW,
    updated_at: NOW,
  },
  provider: {
    id: DEV_USER_IDS.provider,
    email: 'test-leverantor@hjalptorget.dev',
    full_name: 'Sigge Reichard',
    phone: '+46739000002',
    avatar_url: null,
    role: 'provider',
    bankid_verified: true,
    bankid_pnr_hash: null,
    preferred_language: 'sv',
    created_at: NOW,
    updated_at: NOW,
  },
  admin: {
    id: DEV_USER_IDS.admin,
    email: 'test-admin@hjalptorget.dev',
    full_name: 'Sigge Reichard',
    phone: null,
    avatar_url: null,
    role: 'admin',
    bankid_verified: true,
    bankid_pnr_hash: null,
    preferred_language: 'sv',
    created_at: NOW,
    updated_at: NOW,
  },
}

// ── Unified lookup ─────────────────────────────────────────────────────
const ALL = [
  ...Object.values(DEV_PROFILES),
  ...PROVIDER_USER_PROFILES,
  ...CUSTOMER_PROFILES,
]

export function findProfileById(id: string): Profile | undefined {
  return ALL.find((p) => p.id === id)
}
